import { stationsService } from '@/api/stations.service';
import type {
  CreateStationDto,
  FindAllStationsQueryDto,
  Station,
  UpdateStationDto,
} from '@/entities/station/station.entity';
import { removeItemFromQueriesData } from '@/utils/update-query-data';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toRef, toValue } from 'vue';

export const STATIONS_QUERY_KEY = 'stations';

export const useStation = (id: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean>) => {
  return useQuery({
    queryKey: [STATIONS_QUERY_KEY, toRef(id)],
    queryFn: () => stationsService.getOne(toValue(id)),
    enabled: toRef(enabled),
  });
};

export const useStations = (limit = 10) => {
  return useInfiniteQuery({
    queryKey: [STATIONS_QUERY_KEY],
    queryFn: ({ pageParam = 1 }) => stationsService.getAllPaginated({ page: pageParam, limit }),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useStationsWithQuery = (
  query: MaybeRefOrGetter<FindAllStationsQueryDto>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [STATIONS_QUERY_KEY, toRef(query)],
    queryFn: ({ pageParam = 1 }) =>
      stationsService.getAllPaginated({ ...toValue(query), page: pageParam }),
    enabled: toRef(enabled),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useUpdateStation = () => {
  return useMutation({
    mutationKey: [STATIONS_QUERY_KEY],
    mutationFn: ({ id, station }: { id: string; station: UpdateStationDto }) =>
      stationsService.update(id, station),
    onMutate: () => ({
      successMessage: 'Station updated',
      invalidateQueryKeys: [STATIONS_QUERY_KEY],
    }),
  });
};

export const useCreateStation = () => {
  return useMutation({
    mutationKey: [STATIONS_QUERY_KEY],
    mutationFn: (station: CreateStationDto) => stationsService.create(station),
  });
};

export const useDeleteStation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [STATIONS_QUERY_KEY],
    mutationFn: (id: string) => stationsService.remove(id),
    onMutate: (id) => ({ id, successMessage: 'Station deleted' }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      removeItemFromQueriesData<Station>([STATIONS_QUERY_KEY], variables, queryClient);
    },
  });
};

export const useHolidaySubdivisions = (
  countryCode: MaybeRefOrGetter<string | null | undefined>,
  lang: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: ['holiday-subdivisions', toRef(countryCode), toRef(lang)],
    queryFn: () => stationsService.getHolidaySubdivisions(toValue(countryCode)!, toValue(lang)),
    enabled: toRef(enabled),
  });
};

export const useAvailableHolidayRules = (
  countryCode: MaybeRefOrGetter<string | null | undefined>,
  state: MaybeRefOrGetter<string | null | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: ['available-holiday-rules', toRef(countryCode), toRef(state)],
    queryFn: () => stationsService.getAvailableHolidayRules(toValue(countryCode)!, toValue(state)),
    enabled: toRef(enabled),
  });
};
